/**
 * PostStudioCard — the ONE card shell every Post Studio surface sits in.
 *
 * WHY THIS EXISTS (WI-POST-STUDIO-VERIFY-LIVE-AND-REVERT-PENDING-BY-DIFF-001, CEO ruling
 * 2026-07-30 recorded at P-124).
 *
 * This shell was written FOUR times, byte-identically, inside LinkedInPostAgent.jsx — at the
 * pending, saved, posted and rejected cards — and a fifth time inside IdeaTabPanel.jsx when the
 * Ideas tab was moved off its <table>. Five copies of one design is precisely how two surfaces
 * drift apart, and drifting apart is the defect this window was opened to repair: the Pending
 * tab's withheld-draft notice was written as a SIXTH treatment (rounded-xl, a flat border on all
 * four sides, p-4) and so matched nothing else on the screen.
 *
 * The CEO's instruction is "ONE CARD, TWO SURFACES, ONE EFFECT — make the CARD identical, not
 * the surface". A shared component is what makes that enforceable rather than aspirational: a
 * sixth surface is now a USE of the card, and cannot be a sixth copy of it.
 *
 * NO NEW DESIGN. Every value here is lifted verbatim from the existing cards and comes from
 * DS_COLORS / DS_BORDERS. No new token, no new colour, no new radius, no new type scale.
 */

import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import {
    DS_COLORS,
    DS_BORDERS,
    DS_TYPOGRAPHY,
    DS_SPACING,
    DESIGN_INTENTS,
} from '@/config/ui-invariants';

/**
 * PostStudioCardHeader — the SAVED card's header, made field-agnostic.
 *
 * WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001 (CEO ruling: MAKE THE CARD
 * IDENTICAL, NOT THE SURFACE).
 *
 * The previous window matched the SHELL on all five tabs and left three unrelated designs
 * INSIDE it. This is the inside, taken from the reference rather than redrawn.
 *
 * ANATOMY, recorded from the DEPLOYED Saved card on 2026-07-31 and reproduced exactly:
 *
 *   <button aria-expanded>              minHeight 44px, padding 0 24px
 *     <div class="flex-1 min-w-0 py-3">
 *       <div class="truncate">          title — 18px / 600 / DM Sans
 *       <div class="flex gap-2 mt-1.5"> pill row — 12px, radius 9999px, pad 4px 10px
 *       <div class="mt-1 truncate">     ONE truncated summary line (omitted when absent)
 *     <svg>                             caret, rotates 180° when expanded
 *
 * WHY IT TAKES `pills` RATHER THAN A POST. The version this replaces read post-only fields
 * directly — post_type, content_type, ranker_score, article_agent_analysis.recommendation —
 * which is exactly why the Ideas tab could not reuse it and grew its own <table> instead, and
 * why the withheld-draft notice grew a third design. Each surface now computes its OWN facts
 * and hands them over as pills; the CARD is identical, the facts stay the surface's own. That
 * is the ruling, met literally.
 *
 * NO NEW DESIGN. Every class and value below is lifted verbatim from the reference.
 *
 * @param {string}  title        Single line, truncated.
 * @param {Array}   pills        [{ text, intent, bold?, title?, ariaLabel? }] — `intent` is a
 *                               DESIGN_INTENTS key, so no surface picks a colour itself.
 * @param {string}  summaryLine  Optional. One truncated line under the pills.
 */
export function PostStudioCardHeader({
    title,
    pills = [],
    summaryLine,
    isExpanded = false,
    onToggle,
}) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            className="w-full flex items-center cursor-pointer"
            style={{
                background: isExpanded ? DS_COLORS.surface.white : DS_COLORS.surface.warm,
                minHeight: DS_SPACING.minTouchTarget + 'px',
                borderBottom: isExpanded ? 'none' : DS_BORDERS.width + ' solid ' + DS_BORDERS.color,
                padding: '0 ' + DS_SPACING.cardPadding + 'px',
            }}
        >
            <div className="flex-1 min-w-0 py-3">
                <div
                    className="truncate"
                    style={{
                        ...DS_TYPOGRAPHY.listItemTitle,
                        color: DS_COLORS.text.primary,
                        textAlign: 'left',
                    }}
                >{title}</div>
                {pills.length > 0 && (
                    <div className="flex items-center gap-2 mt-1.5">
                        {pills.map((p, i) => {
                            const intent = DESIGN_INTENTS[p.intent] || DESIGN_INTENTS.neutral;
                            return (
                                <span
                                    key={`${p.text}-${i}`}
                                    className={
                                        'inline-block px-2.5 py-1 rounded-full '
                                        + (p.bold ? 'font-bold ' : 'font-semibold ')
                                        + `${intent.bg} ${intent.text}`
                                    }
                                    /* 16px, THE DESIGN AUTHORITY'S FLOOR
                                       (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 7,
                                       CEO ruling 2026-07-31).

                                       These chips carried Tailwind's `text-xs` — 12px — against
                                       DESIGN_LANGUAGE_SKILL.md's "body text 16px minimum",
                                       restated as CLAUDE.md rule 13. The CEO reads this surface
                                       by text-to-speech and residual vision and has asked twice
                                       to be raised off small text; the chips are where the four
                                       facts of every card live, so they are the least defensible
                                       place on the screen to be under the floor.

                                       The size comes from DS_TYPOGRAPHY.bodyMd rather than a
                                       Tailwind step or a literal, so the floor moves here if the
                                       authority ever moves. This is ONE edit reaching all five
                                       tabs — the return the card consolidation was bought for. */
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    }}
                                    title={p.title || undefined}
                                    aria-label={p.ariaLabel || undefined}
                                >
                                    {p.text}
                                </span>
                            );
                        })}
                    </div>
                )}
                {summaryLine && (
                    <div
                        className="mt-1 truncate"
                        /* RAISED WITH THE CHIPS, AND FOR THE SAME REASON (Leg 7).
                           This line carried a hardcoded '12px' — both under the 16px floor AND a
                           literal where every other value on this card comes from a token. The
                           CEO's ruling names the fact chips, but raising them while leaving a
                           second 12px line on the SAME card would be indefensible on a surface
                           he reads by text-to-speech: it is the same floor and the same card.
                           Stated here rather than done quietly, and carried in the report. */
                        style={{
                            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                            color: DS_COLORS.text.secondary,
                            textAlign: 'left',
                        }}
                        title={summaryLine}
                    >
                        {summaryLine}
                    </div>
                )}
            </div>
            <CaretDown
                weight="bold"
                className="w-4 h-4 transition-transform duration-200 flex-shrink-0 ml-3"
                style={{
                    color: DS_COLORS.text.muted,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
            />
        </button>
    );
}

/**
 * @param {'brand'|'muted'} accent  Left-edge accent. 'brand' is the navy resting state every
 *                                  collapsed card carries; 'muted' is the plain border colour
 *                                  the existing cards switch to while expanded. Carried rather
 *                                  than invented — see LinkedInPostAgent.jsx PendingPostCard.
 * @param {boolean} elevated        The card shadow the existing cards raise while expanded.
 */
export default function PostStudioCard({
    accent = 'brand',
    elevated = false,
    className = '',
    style = {},
    children,
    ...rest
}) {
    return (
        <div
            className={`bg-white rounded-2xl overflow-visible relative ${className}`.trim()}
            style={{
                borderLeft: '4px solid ' + (accent === 'muted' ? DS_BORDERS.color : DS_COLORS.border.brand),
                borderTop: DS_BORDERS.width + ' solid ' + DS_BORDERS.color,
                borderRight: DS_BORDERS.width + ' solid ' + DS_BORDERS.color,
                borderBottom: DS_BORDERS.width + ' solid ' + DS_BORDERS.color,
                boxShadow: elevated ? DS_COLORS.shadow.card : 'none',
                ...style,
            }}
            {...rest}
        >
            {children}
        </div>
    );
}
